import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Image,
  Radio,
  Space,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core'
import { observer } from 'mobx-react-lite'
import type { EditorState } from 'prosemirror-state'

import { ScriptDocType } from '@showrunner/codex'

import onecol from '#assets/images/onecol.png'
import twocol from '#assets/images/twocol.png'
import {
  getRevisionsState,
  type Revision,
} from '#choo-app/lib/editor/plugins/revisions'
import { WarningAlert } from '#components/Alerts'
import { ModalShell, useModalControls } from '#components/Modals'
import { RevisionPill } from '#components/ScriptStatusBar/RevisionPill'
import { ILoadedScript, IOrg, useMst } from '#state'
import {
  ScriptPrintPreferences,
  ScriptRevisionPreferences,
  SlotConfig,
  SlotPosition,
} from '#util/zodSchemas/printPreferences'

import {
  CORNER_ITEM_NUMBER,
  getFilename,
  getPrintableScriptData,
  initialSelectedIds,
  PrintableScriptData,
  PrintScriptModalProps,
} from './helpers'
import { SelectRundownScripts } from './SelectRundownScripts'
import { usePrintScripts } from './usePrintScripts'

type DialogueSpacing = 1 | 1.5 | 2
type ColorMode = 'mono' | 'color'
type LayoutMode = 'one' | 'two'
type PreferencesTab = 'all' | 'variety' | 'revisions'
type Props = {
  allPrintableItems: PrintableScriptData[]
  docType: 'rundown' | ScriptDocType
  fileName: string
  enableDuration: boolean
  script?: ILoadedScript
}

const PrintPrefs = observer(function PrintPrefsParams({
  allPrintableItems,
  fileName,
  docType,
  script,
}: Props) {
  const { user, currentOrg } = useMst()
  const { loading, errorMessage, print } = usePrintScripts()
  const prefs = user.scriptPrintPrefs
  const updatePrefs = user.updateScriptPrintPreferences
  const controls = useModalControls()

  const [idsToPrint, setIdsToPrint] = React.useState(
    initialSelectedIds(allPrintableItems),
  )
  const itemsToPrint = allPrintableItems.filter((item) =>
    idsToPrint.includes(item.scriptId),
  )

  const updateHeaderSlot = (position: SlotPosition, config?: SlotConfig) => {
    updatePrefs({
      headers: {
        ...prefs.headers,
        slots: { ...prefs.headers.slots, [position]: config },
      },
    })
  }

  const handlePrint = async (type: 'print' | 'download') => {
    if (itemsToPrint.length > 0) {
      const success = await print({
        fileName,
        printableItems: itemsToPrint,
        type,
      })
      if (success) {
        controls.onClose()
      }
    }
  }

  return (
    <ModalShell
      title={docType === 'rundown' ? 'Print scripts' : 'Print preferences'}
      size={500}
      opened={controls.opened}
      onClose={controls.onClose}
      centered={false}
      loading={loading}
      errorMessage={errorMessage}
    >
      <PagePreferences
        docType={docType}
        prefs={prefs}
        updatePrefs={updatePrefs}
        updateHeaderSlot={updateHeaderSlot}
        idsToPrint={idsToPrint}
        setIdsToPrint={setIdsToPrint}
        allPrintableItems={allPrintableItems}
        currentOrg={currentOrg}
        script={script}
      />
      <Group gap="xs" justify="center">
        <Button
          disabled={itemsToPrint.length === 0}
          type="submit"
          onClick={() => handlePrint('print')}
        >
          Print
        </Button>
        <Button
          disabled={itemsToPrint.length === 0}
          type="submit"
          onClick={() => handlePrint('download')}
        >
          Export PDF
        </Button>
        <Button variant="outline" onClick={controls.onClose}>
          Close
        </Button>
      </Group>
    </ModalShell>
  )
})

export const PrintScriptModal = NiceModal.create((props: Props) => (
  <PrintPrefs {...props} />
))

// See (../readme.md)
// oxlint-disable-next-line react/only-export-components
export const showPrintScriptModal = (params: PrintScriptModalProps) => {
  const enableDuration =
    params.docType === 'script' && params.script.type !== 'screenplay'

  const docType =
    params.docType === 'rundown' ? params.docType : params.script.type
  const script = params.docType === 'script' ? params.script : undefined

  const props: Props = {
    docType,
    allPrintableItems: getPrintableScriptData(params),
    fileName: getFilename(params),
    enableDuration,
    script,
  }

  NiceModal.show(PrintScriptModal, props)
}

interface PagePreferencesProps {
  docType: 'rundown' | ScriptDocType
  prefs: ScriptPrintPreferences
  updatePrefs(updates: Partial<ScriptPrintPreferences>): void
  updateHeaderSlot(position: SlotPosition, config?: SlotConfig): void
  idsToPrint: string[]
  setIdsToPrint(ids: string[]): void
  allPrintableItems: PrintableScriptData[]
  currentOrg?: IOrg
  script?: ILoadedScript
}

const PagePreferences = (props: PagePreferencesProps) => {
  const {
    docType,
    prefs,
    updatePrefs,
    updateHeaderSlot,
    idsToPrint,
    setIdsToPrint,
    allPrintableItems,
    currentOrg,
    script,
  } = props
  const disableStudioPrefs = docType === 'screenplay'
  const disableColumnPrefs = disableStudioPrefs || !prefs.columns
  const varietyFormatName = currentOrg?.studioFormatName ?? 'Variety'
  const editorState = script?.pmEditor.editorState
  const revisions = editorState ? getRevisionsState(editorState) : undefined
  const revisionsResolved = revisions?.tag === 'Resolved'
  const [activeTab, setActiveTab] = React.useState<PreferencesTab>('all')

  function handleTabChange(tab: string | null) {
    if (tab === 'all' || tab === 'variety' || tab === 'revisions') {
      setActiveTab(tab)
    }
  }

  function showVarietyPreferences() {
    setActiveTab('variety')
  }

  return (
    <>
      {docType === 'rundown' && (
        <>
          <SelectRundownScripts
            selectedIds={idsToPrint}
            setSelectedIds={setIdsToPrint}
            allPrintableItems={allPrintableItems}
          />
          <Space h={20} />
          <Checkbox
            label="Include rundown item number in top-right corner"
            checked={!!prefs.headers.slots.rightCorner}
            onChange={(evt) => {
              updateHeaderSlot(
                'rightCorner',
                evt.target.checked ? CORNER_ITEM_NUMBER : undefined,
              )
            }}
          />
          <Divider my="md" />
        </>
      )}
      <Tabs
        value={activeTab}
        allowTabDeactivation={false}
        onChange={handleTabChange}
        keepMountedMode="display-none"
      >
        <Tabs.List>
          <Tabs.Tab value="all">All Scripts</Tabs.Tab>
          <Tabs.Tab value="variety">{varietyFormatName} Scripts</Tabs.Tab>
          {revisionsResolved && (
            <Tabs.Tab value="revisions">Revisions</Tabs.Tab>
          )}
        </Tabs.List>
        <Tabs.Panel value="all" px={20} pt={30} pb={10}>
          <Group gap={60} align="start" justify="center">
            <Radio.Group
              label="Text color"
              value={(prefs.monochrome ? 'mono' : 'color') satisfies ColorMode}
              onChange={(value) =>
                updatePrefs({
                  monochrome: (value as ColorMode) === 'mono',
                })
              }
            >
              <Space h={10} />
              <Stack gap="md">
                <Radio label="Black and white" value="mono" />
                <Radio label="Color" value="color" />
              </Stack>
            </Radio.Group>
            <Stack gap={10}>
              <Text fw="bold" size="sm">
                Pages
              </Text>
              <Checkbox
                label="Include title page(s)"
                checked={prefs.includeTitlePages}
                onChange={(evt) =>
                  updatePrefs({ includeTitlePages: evt.target.checked })
                }
              />
            </Stack>
          </Group>
        </Tabs.Panel>
        <Tabs.Panel value="variety" px={20} pt={30} pb={10}>
          <Group align="start" gap={60} justify="center">
            <Radio.Group
              label="Layout"
              value={(prefs.columns ? 'two' : 'one') satisfies LayoutMode}
              onChange={(value) =>
                updatePrefs({
                  columns: (value as LayoutMode) === 'two',
                })
              }
            >
              <Space h={10} />
              <Stack gap="md">
                <Group>
                  <Radio
                    label="One column"
                    value="one"
                    disabled={disableStudioPrefs}
                  />
                  <Image src={onecol} w={40} />
                </Group>
                <Group>
                  <Radio
                    label="Two column"
                    value="two"
                    disabled={disableStudioPrefs}
                  />
                  <Image src={twocol} w={40} />
                </Group>
              </Stack>
            </Radio.Group>
            <Radio.Group
              label="Right column line spacing"
              value={String(prefs.twoColDialogueSpacing)}
              onChange={(value) =>
                updatePrefs({
                  twoColDialogueSpacing: Number(value) as DialogueSpacing,
                })
              }
            >
              <Space h={10} />
              <Stack gap="md">
                <Radio
                  label="Single spaced"
                  value="1"
                  disabled={disableColumnPrefs}
                />
                <Radio
                  label="1.5 spaced"
                  value="1.5"
                  disabled={disableColumnPrefs}
                />
                <Radio
                  label="Double spaced"
                  value="2"
                  disabled={disableColumnPrefs}
                />
              </Stack>
            </Radio.Group>
          </Group>
        </Tabs.Panel>
        {revisionsResolved && editorState && script && (
          <Tabs.Panel value="revisions" px={20} pt={30} pb={10}>
            <ObservableRevisionsPanel
              scriptId={script.id}
              scriptType={script.type}
              editorState={editorState}
              varietyFormatName={varietyFormatName}
              showVarietyPreferences={showVarietyPreferences}
            />
          </Tabs.Panel>
        )}
      </Tabs>
      <Space />
    </>
  )
}

interface ObservableRevisionsPanelProps {
  scriptId: string
  scriptType: ScriptDocType
  editorState: EditorState
  varietyFormatName: string
  showVarietyPreferences(): void
}

const ObservableRevisionsPanel = observer<ObservableRevisionsPanelProps>(
  (props) => {
    const { user } = useMst()
    const revisions = getRevisionsState(props.editorState)
    const revisionPrefs = user.getScriptRevisionPreferences(props.scriptId)
    const updatePrefs = user.updateScriptRevisionPreferences
    const unsupportedTwoColumnPrinting =
      props.scriptType === 'variety' && user.scriptPrintPrefs.columns

    const updateRevisionPreferences = React.useCallback(
      (updates: Partial<ScriptRevisionPreferences>) => {
        updatePrefs(props.scriptId, updates)
      },
      [updatePrefs, props.scriptId],
    )

    if (revisions.tag !== 'Resolved') return null

    const activeRevision = revisions.revisionsState.revisions.active

    return (
      <RevisionsPanel
        preferences={revisionPrefs}
        activeRevisionHasChanges={revisions.tracker.changes.length > 0}
        activeRevision={activeRevision}
        unsupportedTwoColumnPrinting={unsupportedTwoColumnPrinting}
        varietyFormatName={props.varietyFormatName}
        onUpdatePreferences={updateRevisionPreferences}
        onSelectOneColumnPrinting={props.showVarietyPreferences}
      />
    )
  },
)

ObservableRevisionsPanel.displayName = 'ObservableRevisionsPanel'

interface RevisionsPanelProps {
  preferences: ScriptRevisionPreferences
  activeRevisionHasChanges: boolean
  activeRevision: Revision
  unsupportedTwoColumnPrinting: boolean
  varietyFormatName: string
  onUpdatePreferences(updates: Partial<ScriptRevisionPreferences>): void
  onSelectOneColumnPrinting(): void
}

function RevisionsPanel(props: RevisionsPanelProps) {
  'use memo'

  const preferences = props.preferences
  const updatePreferences = props.onUpdatePreferences
  const disableActiveSetOnly = !props.activeRevisionHasChanges
  const showNoActiveRevisionChangesTooltip = !props.activeRevisionHasChanges

  function handleRevisionRangeChange(value: string) {
    if (value === 'entire' || value === 'revised' || value === 'set') {
      updatePreferences({ revisionRange: value })
    }
  }

  // Switch to "entire" if current selection is disabled
  React.useEffect(() => {
    const shouldReset =
      preferences.revisionRange === 'set' && disableActiveSetOnly
    if (shouldReset) {
      updatePreferences({ revisionRange: 'entire' })
    }
  }, [disableActiveSetOnly, preferences.revisionRange, updatePreferences])

  return (
    <Stack gap={30}>
      {props.unsupportedTwoColumnPrinting && (
        <WarningAlert title="Two-column printing is not supported by Revision Mode">
          To print with Revision Mode enabled, use one-column printing.
          <Stack align="end" mt="xs">
            <WarningAlert.Button onClick={props.onSelectOneColumnPrinting}>
              Edit {props.varietyFormatName} layout
            </WarningAlert.Button>
          </Stack>
        </WarningAlert>
      )}
      <Group gap={30} align="start" justify="center">
        <Radio.Group
          value={props.preferences.revisionRange}
          onChange={handleRevisionRangeChange}
        >
          <Text fw="bold" size="sm">
            Range
          </Text>
          <Space h={10} />
          <Stack gap="xs">
            <Radio
              label="Entire script"
              value="entire"
              disabled={props.unsupportedTwoColumnPrinting}
            />
            <Radio
              label="All revised pages"
              value="revised"
              disabled={props.unsupportedTwoColumnPrinting}
            />
            <Stack align="start" gap={5}>
              <Tooltip
                label={`No ${props.activeRevision.label} revisions yet`}
                disabled={!showNoActiveRevisionChangesTooltip}
              >
                <Radio
                  label="Active revision set only"
                  value="set"
                  disabled={
                    disableActiveSetOnly || props.unsupportedTwoColumnPrinting
                  }
                />
              </Tooltip>
              <Box ml={30}>
                <RevisionPill {...props.activeRevision} />
              </Box>
            </Stack>
          </Stack>
        </Radio.Group>
        <Stack gap={10}>
          <Text fw="bold" size="sm">
            Color
          </Text>
          <Checkbox
            label="Full page revision color"
            checked={props.preferences.revisionShowPageColors}
            disabled={props.unsupportedTwoColumnPrinting}
            onChange={(evt) =>
              props.onUpdatePreferences({
                revisionShowPageColors: evt.target.checked,
              })
            }
          />
        </Stack>
      </Group>
    </Stack>
  )
}
